import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Users, Filter, ShieldCheck, ShieldAlert, Shield } from "lucide-react";

const BAND_LABEL = {
  ready: "Ready",
  nearly_ready: "Nearly Ready",
  action_required: "Action Required",
  blocked: "Blocked",
};
const BAND_CHIP = {
  ready: "bg-emerald-100 text-emerald-700 border-emerald-200",
  nearly_ready: "bg-sky-100 text-sky-700 border-sky-200",
  action_required: "bg-amber-100 text-amber-800 border-amber-200",
  blocked: "bg-rose-100 text-rose-700 border-rose-200",
};
const STATUS_CHIP = {
  valid: "bg-emerald-50 text-emerald-700",
  expiring_soon: "bg-amber-50 text-amber-700",
  expired: "bg-rose-50 text-rose-700",
  missing: "bg-slate-100 text-slate-600",
};

const FILTERS = ["all", "ready", "nearly_ready", "action_required", "blocked"];

/**
 * Manager-facing Workforce Readiness panel (OCI V2.1A).
 * One row per fundraiser. Filterable by passport band.
 */
export function WorkforceReadinessPanel({ campaignId = null }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (campaignId) params.set("campaignId", campaignId);
        if (filter !== "all") params.set("bandFilter", filter);
        const r = await apiClient.get(
          `/workforce/dashboard/workforce-readiness?${params.toString()}`
        );
        if (active) setData(r.data);
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [filter, campaignId]);

  return (
    <Card
      className="border-slate-200 shadow-none"
      data-testid="workforce-readiness-panel"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-700" />
            <h3 className="font-heading font-bold text-slate-900">
              Workforce Readiness
            </h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Filter className="h-3.5 w-3.5" />
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`readiness-filter-${f}`}
                className={`px-2.5 py-1 rounded-full border transition-colors ${
                  filter === f
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {f === "all" ? "All" : BAND_LABEL[f]}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="h-32 animate-pulse bg-slate-100 rounded-lg" />
        )}

        {!loading && data && data.rows.length === 0 && (
          <div className="text-center py-8 text-slate-500 text-sm">
            No fundraisers match this filter.
          </div>
        )}

        {!loading && data && data.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="readiness-table"
            >
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Fundraiser</th>
                  <th className="py-2 pr-3">Score</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Missing</th>
                  <th className="py-2 pr-3">DBS</th>
                  <th className="py-2 pr-3">RTW</th>
                  <th className="py-2 pr-3">Campaign Eligible</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(row => (
                  <tr
                    key={row.userId}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`readiness-row-${row.userId}`}
                  >
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-slate-900">
                        {row.name || "—"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {row.email}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 font-bold text-slate-900">
                      {row.passportScore}%
                    </td>
                    <td className="py-2.5 pr-3">
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full border font-medium ${BAND_CHIP[row.passportStatus]}`}
                      >
                        {BAND_LABEL[row.passportStatus]}
                      </span>
                      {row.managerApproved && (
                        <span
                          className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full border font-medium bg-emerald-50 text-emerald-700 border-emerald-200"
                          title={row.overrideApplied ? "Manager approved — override applied (compliance items still outstanding)" : "Manager approved"}
                          data-testid={`readiness-approved-${row.userId}`}
                        >
                          Approved{row.overrideApplied ? " (override)" : ""}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-xs text-slate-600 max-w-[200px]">
                      {row.actionCount === 0 ? (
                        <span className="text-emerald-700 font-medium">
                          —
                        </span>
                      ) : (
                        <span title={row.missingActions.join(", ")}>
                          {row.actionCount} action{row.actionCount === 1 ? "" : "s"}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <span
                        className={`px-2 py-0.5 text-[10px] uppercase rounded ${STATUS_CHIP[row.dbsStatus] || STATUS_CHIP.missing}`}
                      >
                        {row.dbsStatus}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span
                        className={`px-2 py-0.5 text-[10px] uppercase rounded ${STATUS_CHIP[row.rtwStatus] || STATUS_CHIP.missing}`}
                      >
                        {row.rtwStatus}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      {row.campaignEligible ? (
                        <ShieldCheck className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <ShieldAlert className="h-4 w-4 text-rose-600" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
